import { ActionIcon, Box, Collapse, Group, Stack } from "@mantine/core";
import { IconChevronDown } from "@tabler/icons-react";
import cx from "clsx";
import React, { Children, PropsWithChildren, ReactElement, ReactNode, useMemo, useState } from "react";
import classes from "./FoldableSection.module.css";

interface FoldableSectionCompound {
  Header: typeof FoldableSectionHeader;
  Body: typeof FoldableSectionBody;
}

type FoldableSectionProps = {
  folded?: boolean;
  /** If true, clicking the header row toggles collapse. */
  headerClick?: boolean;
  /** Optional: start controlled from outside; if you want fully controlled, pass folded and handle onFoldChange */
  onFoldChange?: (collapsed: boolean) => void;
  /** If provided, persist collapsed state to localStorage under this key. */
  storageKey?: string;
  className?: string;
  style?: React.CSSProperties;
};

const FoldableSection: React.FC<React.PropsWithChildren<FoldableSectionProps>> & FoldableSectionCompound = ({
  children,
  folded = false,
  headerClick = false,
  onFoldChange,
  storageKey,
  className,
  style,
}) => {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (storageKey) {
      try {
        const arr: string[] = JSON.parse(localStorage.getItem("foldedSections") || "[]");
        if (arr.includes(storageKey)) return true;
      } catch {}
    }
    return folded;
  });

  // Sync with parent's folded prop, but skip the initial render so we don't
  // overwrite the value restored from localStorage.
  const isFirstRender = React.useRef(true);

  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    setCollapsed(folded);
  }, [folded]);

  const { headerElement, bodyElement } = useMemo(() => {
    let header: ReactNode = null;
    let body: ReactNode = null;

    Children.forEach(children, (child) => {
      const el = child as ReactElement | null;
      if (!el) return;

      if (el.type === FoldableSectionHeader) header = el;
      if (el.type === FoldableSectionBody) body = el;
    });

    // Fallback to positional if not wrapped in Header/Body
    const arr = Children.toArray(children);
    if (!header && arr[0]) header = arr[0] as ReactNode;
    if (!body && arr[1]) body = arr[1] as ReactNode;

    return { headerElement: header, bodyElement: body };
  }, [children]);

  const toggle = () => {
    setCollapsed((v) => {
      const next = !v;
      onFoldChange?.(next);

      if (storageKey) {
        try {
          const arr: string[] = JSON.parse(localStorage.getItem("foldedSections") || "[]");
          const updated = next ? [...new Set([...arr, storageKey])] : arr.filter((k) => k !== storageKey);

          localStorage.setItem("foldedSections", JSON.stringify(updated));
        } catch {}
      }
      return next;
    });
  };

  return (
    <Stack gap={6} className={cx(classes.root, className)} style={style}>
      <Group
        gap="xs"
        align="center"
        wrap="nowrap"
        className={cx(classes.headerRow, headerClick && classes.headerClickable)}
        onClick={headerClick ? toggle : undefined}
      >
        <ActionIcon
          variant="subtle"
          radius="xl"
          aria-label={collapsed ? "Expand section" : "Collapse section"}
          onClick={(e) => {
            e.stopPropagation(); // don’t double-toggle when headerClick=true
            toggle();
          }}
          className={cx(classes.chevronBtn, !collapsed && classes.chevronOpen)}
        >
          <IconChevronDown size={18} />
        </ActionIcon>

        <Box className={classes.headerContent}>{headerElement}</Box>
      </Group>

      <Collapse in={!collapsed} transitionDuration={240} transitionTimingFunction="ease">
        <Box className={classes.body}>{bodyElement}</Box>
      </Collapse>
    </Stack>
  );
};

const FoldableSectionHeader: React.FC<PropsWithChildren<any>> = ({ children }) => <>{children}</>;
const FoldableSectionBody: React.FC<PropsWithChildren<any>> = ({ children }) => <>{children}</>;

FoldableSection.Header = FoldableSectionHeader;
FoldableSection.Body = FoldableSectionBody;

export default FoldableSection;
